import { ApolloLink, Operation, NextLink, Observable } from '@apollo/client';
import { 
  actionRequiresAuth 
} from '@src/Services/AccessControl/accessRules';
import type { RootState } from '@src/Store';
import { memberTransformer } from '@src/Api/Umbraco/ContentManagmentApi/endpoints/members.endpoint';

/**
 * Apollo Link pour sécuriser les requêtes GraphQL Umbraco
 * 
 * Similaire à authGuardBaseQuery mais pour Apollo Client
 * Intercepte les requêtes GraphQL et vérifie l'authentification
 */

// Configuration des requêtes nécessitant une authentification
const PROTECTED_GRAPHQL_OPERATIONS = {
  // 🎵 Requêtes de players (accès protégé)
  'RadioByUrl': 'player-access',
  'RadioById': 'player-access', // Radio par ID
  'PodcastByUrl': 'player-access', 
  // 'ChannelByUrl': 'player-access',
  'VODByUrl': 'player-access',
  // 'GetMainContentListById': 'player-access',
  // 'ContentIdByEndUrl': 'player-access',
  'PlayListById': 'player-access', // Playlists radio/audio
  
  // 📺 Requêtes vidéo/collection (accès protégé)
  // 'CollectionByUrlEnd': 'player-access',
  'CollectionById': 'player-access',
  
  // 📚 Requêtes de cours (accès protégé)
  'CourseByUrl': 'course-access',
  // 'CourseByEndUrl': 'course-access',
  'CourseByCategoryUrl': 'course-access',
  'CourseUnitByUrl': 'course-access',
  // 'CourseUnitByEndUrl': 'course-access',
  
  // 👤 Requêtes de profil (accès protégé)
  'UserProfile': 'profile-access',
  'UserBookmarks': 'bookmark-access',
  'UserProgress': 'course-progress',
  
  // 💼 Requêtes de jobs (accès protégé)
  'JobByUrl': 'job-access',
  'JobTrainingByUrl': 'job-access',
  // 'JobTrainingByCourse': 'job-access',
  'CourseUnitByEndUrl': 'job-access',
  
  // 🎯 Requêtes de quiz/survey (accès protégé)
  // 'QuizPageQuestionByUrlEnd': 'quiz-access',
  'QuizPageById': 'quiz-access',
  'QuizByUrl': 'quiz-access',
  'SurveyByUrl': 'survey-access',
  
  // 📰 Requêtes d'actualités (accès protégé)
  'NewsByUrlEndUrl': 'news-access',
  'NewsByUrl': 'news-access',
  'NewsById': 'news-access',
  'News': 'news-access',
  // 'NewsCategoryByUrl': 'news-access',
  'NewsCategoryByUrlWithRegion': 'news-access',
  
  // 📥 Requêtes de téléchargement (premium)
  'DownloadContent': 'download-content',
  'CourseDownload': 'download-content',
  'JobDownload': 'download-content',
} as const;

type ProtectedOperation = keyof typeof PROTECTED_GRAPHQL_OPERATIONS;

/**
 * Vérifie si une opération GraphQL nécessite une authentification
 */
const isProtectedOperation = (operationName: string): boolean => {
  return operationName in PROTECTED_GRAPHQL_OPERATIONS;
};

/**
 * Obtient l'action d'authentification pour une opération
 */
const getAuthAction = (operationName: string): string | null => {
  if (isProtectedOperation(operationName)) {
    return PROTECTED_GRAPHQL_OPERATIONS[operationName as ProtectedOperation];
  }
  return null;
};

/**
 * Factory pour créer un Apollo Link d'authentification avec accès au store
 */
export const createAuthGuardLink = (store?: { getState: () => RootState }) => {
  
  /**
   * Vérifie l'état d'authentification de l'utilisateur
   * 
   * Logique simplifiée compatible avec le feature flip anonymous_auth :
   * - Si member existe → utilisateur authentifié (accès aux ressources protégées)
   * - Sinon → utilisateur anonyme/public (pas d'accès aux ressources protégées)
   * 
   * Note: Le feature flip anonymous_auth détermine si un utilisateur Firebase anonyme
   * est créé ou non, mais les règles d'accès restent basées sur l'existence de member
   */
  const checkAuthState = (): { isLoggedIn: boolean; isAnonymous: boolean } => {
    try {
      if (store) {
        const state = store.getState();
        let member = state.member?.member;
        
        // Appliquer la même logique que le sélecteur useMember()
        if (member && !member.memberDatasId) {
          member = memberTransformer(member);
        }
        
        // Logique simplifiée : si member existe, utilisateur authentifié
        const isLoggedIn = !!member;
        const isAnonymous = !isLoggedIn;
        
        return { isLoggedIn, isAnonymous };
      }
      
      console.warn('🚨 [AuthGuardLink] No Redux store provided - this should not happen');
      return { isLoggedIn: false, isAnonymous: true };
    } catch (error) {
      console.warn('[AuthGuardLink] Cannot access auth state:', error);
      return { isLoggedIn: false, isAnonymous: true };
    }
  };

/**
 * Déclenche un événement d'authentification requis
 */
const triggerAuthRequired = (operation: Operation, actionId: string) => {
  // Déclencher l'événement personnalisé pour le GlobalAuthGuard
  const authEvent = new CustomEvent('authRequired', {
    detail: {
      actionId,
      operationType: 'GraphQL',
      operationName: operation.operationName,
      method: 'POST',
      endpoint: '/graphql',
      reason: `GraphQL operation requires authentication: ${operation.operationName}`,
    },
  });

  window.dispatchEvent(authEvent);
};

  /**
   * Apollo Link d'authentification pour les requêtes GraphQL
   */
  return new ApolloLink((operation: Operation, forward: NextLink) => {
  const operationName = operation.operationName;

  // Ignorer les opérations sans nom ou les introspections
  if (!operationName || operationName.startsWith('IntrospectionQuery')) {
    return forward(operation);
  }

  console.log('🔍 [AuthGuardLink] Checking operation:', operationName);

  // Vérifier si l'opération nécessite une authentification
  const authAction = getAuthAction(operationName);
  
  if (authAction) {
    const authRule = actionRequiresAuth(authAction);
    
    if (authRule?.requiresAuth) {
      const { isLoggedIn, isAnonymous } = checkAuthState();
      
      // Si l'utilisateur n'est pas connecté ou est anonyme
      if (!isLoggedIn || isAnonymous) {
        console.log('🚫 [AuthGuardLink] BLOCKING operation:', operationName);
        
        // Déclencher l'événement d'authentification
        triggerAuthRequired(operation, authAction);
        
        // Retourner immédiatement une erreur pour bloquer complètement la requête
        return new Observable(observer => {
          // Compléter immédiatement avec une erreur
          observer.error(new Error(`Authentication required for operation: ${operationName}`));
          observer.complete();
        });
      } else {
        console.log('✅ [AuthGuardLink] ALLOWING operation:', operationName);
      }
    }
  } else {
    console.log('🔓 [AuthGuardLink] Public operation, allowing:', operationName);
  }

    // Continuer avec l'opération si l'auth est OK
    return forward(operation);
  });
};

// La fonction updateGraphQLAuthState n'est plus nécessaire
// L'Apollo Link utilise maintenant directement le store Redux injecté

// Export de compatibilité (sans store - ne devrait plus être utilisé)
export const authGuardLink = createAuthGuardLink();

export default createAuthGuardLink; 